import { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { useProjects } from '@/hooks/useProjects'
import { useTimeTracking } from '@/hooks/useTimeTracking'
import { useTimeEntriesCache } from '@/hooks/useTimeEntriesCache'
import { CreateProjectInput, UpdateProjectInput } from '@/services/projects'
import { Project } from '../../../shared/types'
import { useAuth } from '@/hooks/useAuth'
import { useUserSettingsContext } from '@/hooks/useUserSettingsContext'
import ProjectBudgetChart from '@/components/ProjectBudgetChart'
import ConfirmationDialog from '@/components/ConfirmationDialog'
import CustomDropdown from '@/components/CustomDropdown'
import CustomPagination from '@/components/CustomPagination'
import ProjectForm from '@/components/ProjectForm'
import { formatCurrency, formatHourlyRate } from '@/utils/currency'

/** Renders location items (📍 Name) hiding any that would overflow the line. */
function LocationsRow({ locations, color }: { locations: string[]; color: string }) {
  const containerRef = useRef<HTMLSpanElement>(null)
  const [visibleCount, setVisibleCount] = useState(locations.length)

  useLayoutEffect(() => {
    const measure = () => {
      const container = containerRef.current
      if (!container) return
      const cRight = container.getBoundingClientRect().right
      const items = container.querySelectorAll<HTMLElement>('[data-loc]')
      let count = 0
      for (const item of items) {
        if (item.getBoundingClientRect().right <= cRight + 1) count++
        else break
      }
      setVisibleCount(count)
    }
    measure()
    const ro = new ResizeObserver(measure)
    if (containerRef.current) ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [])

  return (
    <span ref={containerRef} className="inline-flex items-center gap-x-2 min-w-0 overflow-hidden">
      {locations.slice(0, visibleCount).map(name => (
        <span key={name} data-loc="" className="inline-flex items-center gap-1 flex-shrink-0">
          <svg className="w-3 h-3 flex-shrink-0" style={{ color }} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
          {name}
        </span>
      ))}
    </span>
  )
}

export default function Projects() {
  const { user } = useAuth()
  const {
    projects,
    allProjects,
    loading,
    error,
    createProject,
    updateProject,
    deleteProject,
    restoreProject,
    pagination,
    updateFilters,
    filters
  } = useProjects({ loadInactive: true, initialStatus: 'active' })
  const { runningEntry, getCurrentDuration, formatDuration } = useTimeTracking(!!user)
  // loadEntriesForProject is required: called after restore so the live cache
  // resumes computing duration for the project instead of relying on totalDuration.
  // removeEntriesByProjectId is required: called after archive/delete so the cache
  // no longer contributes stale entries to the archived project's totalDuration display.
  const { allEntries: timeEntries, removeEntriesByProjectId, loadEntriesForProject } = useTimeEntriesCache()
  const { displaySettings } = useUserSettingsContext()
  const budgetWarningColor = displaySettings.budgetWarningColor
  const [showForm, setShowForm] = useState(false)
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([])
  
  // Confirmation dialog states
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean
    project: Project | null
    type: 'archive' | 'delete' | 'restore' | 'hardDelete'
  }>({
    isOpen: false,
    project: null,
    type: 'delete'
  })
  const [recentlyEditedProjects, setRecentlyEditedProjects] = useState<Set<string>>(new Set())

  // Detect projects that were auto-archived server-side (via GET /projects trigger).
  // When allProjects updates and a project transitions active → archived, evict its
  // entries from the cache so they stop appearing on Entries/Dashboard pages.
  const prevProjectStatusRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    if (!allProjects.length) return;
    const prev = prevProjectStatusRef.current;
    if (prev.size > 0) {
      allProjects
        .filter(p => p.status === 'archived' && prev.get(p.id) === 'active')
        .forEach(p => removeEntriesByProjectId(p.id));
    }
    prevProjectStatusRef.current = new Map(allProjects.map(p => [p.id, p.status ?? 'active']));
  }, [allProjects, removeEntriesByProjectId]);

  // Handle URL parameters for project pre-selection
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const projectId = urlParams.get('project')
    
    if (projectId) {
      // Check if the project exists in allProjects (which includes all projects, not just filtered ones)
      const projectExists = allProjects.some((p: Project) => p.id === projectId)
      if (projectExists) {
        // Set the selected project ID for filtering
        setSelectedProjectIds([projectId])
        updateFilters({ projectIds: [projectId], page: 1 })
      } else {
        // Project doesn't exist, clear the filter
        setSelectedProjectIds([])
        updateFilters({ projectIds: [], page: 1 })
      }
    } else {
      // No project parameter in URL, clear the filter
      setSelectedProjectIds([])
      updateFilters({ projectIds: [], page: 1 })
    }
  }, [allProjects, updateFilters])



  // Generate intelligent unified filter options
  const generateUnifiedFilterOptions = () => {
    const options = [
      // Status-based options
      { value: 'active', label: 'Active Projects', group: 'Status' },
      { value: 'archived', label: 'Archived Projects', group: 'Status' },
      
      // Type-based options (only for admin users)
      ...(user?.role === 'admin' ? [
        { value: 'personal', label: 'Personal Projects', group: 'Type' },
        { value: 'shared', label: 'Shared Projects', group: 'Type' }
      ] : [])
    ]
    
    return options
  }

  // Handle unified filter change with intelligent combination logic
  const handleUnifiedFilterChange = (values: string[]) => {
    if (values.length === 0) {
      // Reset to default
      updateFilters({ status: 'all', type: 'all', page: 1 })
      return
    }
    
    // Separate status and type selections
    const statusSelections = values.filter(v => ['active', 'archived'].includes(v))
    const typeSelections = values.filter(v => ['personal', 'shared'].includes(v))
    
    // Determine final status and type
    let finalStatus: 'active' | 'archived' | 'all' = 'all'
    let finalType: 'personal' | 'shared' | 'all' = 'all'
    
    // Status logic: status options are mutually exclusive (radio button behavior)
    if (statusSelections.length > 0) {
      // Use the last selected status (most recent selection)
      const lastStatusSelection = statusSelections[statusSelections.length - 1]
      finalStatus = lastStatusSelection as 'active' | 'archived' | 'all'
    }
    
    // Type logic: type options are mutually exclusive (radio button behavior)
    if (typeSelections.length > 0) {
      // Use the last selected type (most recent selection)
      const lastTypeSelection = typeSelections[typeSelections.length - 1]
      finalType = lastTypeSelection as 'personal' | 'shared'
    }
    
    updateFilters({ 
      status: finalStatus, 
      type: finalType, 
      page: 1 
    })
  }

  // Get current unified filter values for multi-select
  const getCurrentUnifiedFilterValue = () => {
    const status = filters?.status || 'all'
    const type = filters?.type || 'all'
    const values: string[] = []
    
    // Add status selection (only if not 'all')
    if (status !== 'all') {
      values.push(status)
    }
    
    // Add type selection (only for admin users)
    if (user?.role === 'admin' && type !== 'all') {
      values.push(type)
    }
    
    return values
  }

  // Handle page change
  const handlePageChange = (page: number) => {
    updateFilters({ page })
  }

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    updateFilters({ limit: newPageSize, page: 1 })
  }

  // Handle project selection change
  const handleProjectSelectionChange = (projectIds: string[]) => {
    setSelectedProjectIds(projectIds)
    updateFilters({ projectIds, page: 1 })
    
    // Update URL to reflect the filter change
    const url = new URL(window.location.href)
    if (projectIds.length > 0) {
      url.searchParams.set('project', projectIds[0]) // For now, only use the first project
    } else {
      url.searchParams.delete('project')
    }
    window.history.replaceState({}, '', url.toString())
  }

  const handleCreate = async (input: CreateProjectInput | UpdateProjectInput) => {
    const result = await createProject(input as CreateProjectInput)
    
    // Add to recently edited projects for highlighting (treating new projects as "edited" for the glow effect)
    setRecentlyEditedProjects(prev => new Set([...prev, result.project.id]))
    
    // Scroll to the newly created card
    setTimeout(() => {
      const cardElement = document.querySelector(`[data-project-id="${result.project.id}"]`)
      if (cardElement && cardElement.scrollIntoView) {
        cardElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        })
      }
    }, 100)
    
    // Remove highlighting after 3 seconds
    setTimeout(() => {
      setRecentlyEditedProjects(prev => {
        const newSet = new Set(prev)
        newSet.delete(result.project.id)
        return newSet
      })
    }, 3000)
    
    setShowForm(false)
  }

  const handleUpdate = async (input: CreateProjectInput | UpdateProjectInput) => {
    if (editingProject) {
      await updateProject(editingProject.id, input as UpdateProjectInput)
      
      // Add to recently edited projects for highlighting
      setRecentlyEditedProjects(prev => new Set([...prev, editingProject.id]))
      
      // Scroll to the edited card
      setTimeout(() => {
        const cardElement = document.querySelector(`[data-project-id="${editingProject.id}"]`)
        if (cardElement && cardElement.scrollIntoView) {
          cardElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          })
        }
      }, 100)
      
      // Remove highlighting after 3 seconds
      setTimeout(() => {
        setRecentlyEditedProjects(prev => {
          const newSet = new Set(prev)
          newSet.delete(editingProject.id)
          return newSet
        })
      }, 3000)
      
      setEditingProject(null)
    }
  }

  const handleDelete = async (project: Project) => {
    const hasEntries = timeEntries.some(entry => entry.projectId === project.id)
    const hasRunningEntry = runningEntry && runningEntry.projectId === project.id
    
    if (hasRunningEntry) {
      alert(`Cannot delete "${project.name}" because it has a running time entry. Please stop the timer first.`)
      return
    }
    
    if (hasEntries) {
      setDeleteDialog({
        isOpen: true,
        project,
        type: 'archive'
      })
    } else {
      setDeleteDialog({
        isOpen: true,
        project,
        type: 'delete'
      })
    }
  }

  const handleEdit = (project: Project) => {
    setEditingProject(project)
  }

  const handleProjectClick = (project: Project) => {
    setEditingProject(project)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  // Memoize project hours calculations to improve performance
  const projectHoursMap = useMemo(() => {
    const hoursMap = new Map<string, string>()
    projects.forEach(project => {
      const projectEntries = timeEntries.filter(entry => entry.projectId === project.id)
      if (projectEntries.length > 0) {
        const totalSeconds = projectEntries.reduce((total, entry) => {
          return total + getCurrentDuration(entry)
        }, 0)
        hoursMap.set(project.id, formatDuration(totalSeconds))
      } else if (project.totalDuration !== undefined) {
        hoursMap.set(project.id, formatDuration(project.totalDuration))
      }
    })
    return hoursMap
  }, [projects, timeEntries, getCurrentDuration, formatDuration])

  const getProjectTotalHours = (projectId: string) => {
    return projectHoursMap.get(projectId) || '0h 0m'
  }

  // Memoize per-project date ranges (first entry → last entry)
  const projectDateRangeMap = useMemo(() => {
    const rangeMap = new Map<string, string>()
    const formatMonthYear = (iso: string) =>
      new Date(iso).toLocaleDateString([], { month: '2-digit', year: '2-digit' })

    projects.forEach(project => {
      const starts = timeEntries
        .filter(e => e.projectId === project.id)
        .map(e => e.startTime)
      if (starts.length === 0) {
        rangeMap.set(project.id, '—')
      } else {
        const first = starts.reduce((a, b) => (a < b ? a : b))
        const last  = starts.reduce((a, b) => (a > b ? a : b))
        const firstFmt = formatMonthYear(first)
        const lastFmt  = formatMonthYear(last)
        rangeMap.set(project.id, firstFmt === lastFmt ? firstFmt : `${firstFmt} – ${lastFmt}`)
      }
    })
    return rangeMap
  }, [projects, timeEntries])

  // Restore an archived project
  const handleRestore = async (project: Project) => {
    setDeleteDialog({
      isOpen: true,
      project,
      type: 'restore'
    })
  }

  // Hard delete an archived project with no entries
  const handleHardDelete = async (project: Project) => {
    setDeleteDialog({
      isOpen: true,
      project,
      type: 'hardDelete'
    })
  }

  // Handle confirmation dialog actions
  const handleConfirmAction = async () => {
    if (!deleteDialog.project) return

    try {
      switch (deleteDialog.type) {
        case 'archive':
          await deleteProject(deleteDialog.project.id)
          // Must evict from cache: archived projects fall back to server-computed
          // totalDuration; stale cache entries would double-count on next load.
          removeEntriesByProjectId(deleteDialog.project.id)
          break
        case 'delete':
          await deleteProject(deleteDialog.project.id) // Hard delete — no entries, no-op safe
          removeEntriesByProjectId(deleteDialog.project.id)
          break
        case 'restore':
          await restoreProject(deleteDialog.project.id)
          // Must reload into cache: active projects compute duration from live cache
          // (includes running entries in real-time). Without this the project shows
          // 0h until a full page refresh triggers loadAllEntries.
          await loadEntriesForProject(deleteDialog.project.id)
          break
        case 'hardDelete':
          await deleteProject(deleteDialog.project.id)
          removeEntriesByProjectId(deleteDialog.project.id)
          break
      }
    } catch {
      // Error is handled by the hook
    } finally {
      setDeleteDialog({ isOpen: false, project: null, type: 'delete' })
    }
  }

  const handleCancelAction = () => {
    setDeleteDialog({ isOpen: false, project: null, type: 'delete' })
  }





  if (loading && projects.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div data-testid="loading-spinner" className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Filters and Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4 flex-1">
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              New Project
            </button>
            {/* Project Filter */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Project:</span>
              <CustomDropdown
                id="projects-project"
                label=""
                placeholder="All Projects"
                options={projects.map(project => ({ value: project.id, label: project.name }))}
                selectedValues={selectedProjectIds}
                onSelectionChange={handleProjectSelectionChange}
                multiSelect={true}
                maxSelections={6}
                showClearAll={true}
                clearAllText="Clear All"
                searchable={true}
                searchPlaceholder="Search projects..."
                minWidth="min-w-[250px]"
              />
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Filter:</span>
              <CustomDropdown
                id="project-unified-filter"
                label=""
                placeholder="Select filter"
                options={generateUnifiedFilterOptions()}
                selectedValues={getCurrentUnifiedFilterValue()}
                onSelectionChange={handleUnifiedFilterChange}
                multiSelect={true}
                transparency="low"
                minWidth="min-w-[280px]"
                showGroups={true}
                showClearAll={true}
                clearAllText="Clear All"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2 flex-shrink-0">
            <CustomPagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              hasNextPage={pagination.hasNextPage}
              hasPrevPage={pagination.hasPrevPage}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSize={filters?.limit || 10}
              className="mb-0"
              transparency="medium"
              minWidth="min-w-[280px]"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}



      {projects.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="text-gray-500 mb-4">
            {filters?.status === 'active' ? 'No active projects' : 
             filters?.status === 'archived' ? 'No archived projects' : 
             'No projects yet'}
          </div>
          <p className="text-sm text-gray-400 mb-6">
            {filters?.status === 'active' ? 'Create a new project to get started' :
             filters?.status === 'archived' ? 'Archived projects will appear here' :
             'Create your first project to get started'}
          </p>
          {filters?.status === 'active' && (
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Create Project
            </button>
          )}
          {/* Add note about missing projects */}
          {filters?.status === 'all' && projects.length === 0 && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-700">
                If you're looking for a specific project that appears in your time entries but not here, 
                it might be archived or shared. Try checking the "Archived" filter or contact your administrator.
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div
              key={project.id}
              data-project-id={project.id}
              className={`rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-300 cursor-pointer flex flex-col ${
                project.status === 'archived' 
                  ? 'bg-gray-50' 
                  : 'bg-white'
              } ${
                recentlyEditedProjects.has(project.id) 
                  ? 'ring-2 ring-blue-500 shadow-lg' 
                  : ''
              }`}
              style={recentlyEditedProjects.has(project.id) ? {
                backgroundColor: `${project.color}10`,
                borderColor: project.color,
                boxShadow: `0 10px 25px -5px ${project.color}40, 0 10px 10px -5px ${project.color}20`
              } : {}}
              onClick={() => handleProjectClick(project)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3 min-w-0 flex-1">
                  <div
                    className="w-1 h-6 rounded-full flex-shrink-0"
                    style={{ backgroundColor: project.color }}
                  />
                  <h3 className="font-medium text-gray-900 truncate pr-1">{project.name}</h3>
                </div>
                <div className="flex space-x-1">
                  {/* Archive symbol for archived projects - only show in "all" view, not in "archived" filter */}
                  {project.status === 'archived' && filters?.status === 'all' && (
                    <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <title>Archived project</title>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                  )}
                  {/* Restore button for archived projects */}
                  {project.status === 'archived' && (
                    <button
                      onClick={e => { e.stopPropagation(); handleRestore(project); }}
                      className="text-gray-400 hover:text-green-600 focus:outline-none focus:text-green-600"
                      title="Restore project"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                  )}
                  {/* Trash button for archived projects with no entries */}
                  {project.status === 'archived' && !timeEntries.some(entry => entry.projectId === project.id) && (
                    <button
                      onClick={e => { e.stopPropagation(); handleHardDelete(project); }}
                      className="text-gray-400 hover:text-red-600 focus:outline-none focus:text-red-600"
                      title="Delete archived project"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                  {/* Edit and archive/delete for active projects */}
                  {project.status !== 'archived' && (
                    <>
                      {/* Entries icon - link to entries page with project pre-selected - only show if project has time entries */}
                      {timeEntries.some(entry => entry.projectId === project.id) && (
                        <Link
                          to={`/entries?project=${project.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-gray-400 hover:text-blue-600 focus:outline-none focus:text-blue-600"
                          title="View project entries"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                          </svg>
                        </Link>
                      )}
                      {/* Reporting icon - only show if project has time entries */}
                      {timeEntries.some(entry => entry.projectId === project.id) && (
                        <Link
                          to={`/reporting?project=${project.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-gray-400 hover:text-green-600 focus:outline-none focus:text-green-600"
                          title="View project reports"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </Link>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleEdit(project)
                        }}
                        className="text-gray-400 hover:text-blue-600 focus:outline-none focus:text-blue-600"
                        title="Edit project"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      {(() => {
                        const hasEntries = timeEntries.some(entry => entry.projectId === project.id)
                        const hasRunningEntry = runningEntry && runningEntry.projectId === project.id
                        
                        if (hasRunningEntry) {
                          return (
                            <button
                              className="text-gray-400 cursor-not-allowed"
                              title="Cannot archive/delete project with running timer"
                              disabled
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </button>
                          )
                        }
                        
                        return hasEntries ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDelete(project)
                            }}
                            className="text-gray-400 hover:text-orange-600 focus:outline-none focus:text-orange-600"
                            title="Archive project (has time entries)"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDelete(project)
                            }}
                            className="text-gray-400 hover:text-red-600 focus:outline-none focus:text-red-600"
                            title="Delete project"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )
                      })()}
                    </>
                  )}
                </div>
              </div>

              <div className="mb-3 min-h-[1.25rem]">
                {project.description ? (
                  <p className="text-sm text-gray-600 truncate">{project.description}</p>
                ) : (
                  <div className="text-sm text-gray-400 italic">No description</div>
                )}
              </div>

              <div className="border-t border-gray-100 mb-3" />

              {/* Row 1: Date range • Locations — icon + value, no labels */}
              {(() => {
                const color = project.color || '#6B7280'
                const dateRange = projectDateRangeMap.get(project.id)
                const hasDate = dateRange && dateRange !== '—'
                const locations = project.locations ?? []
                if (!hasDate && locations.length === 0) return null
                return (
                  <div className="flex items-center gap-x-2 mb-1.5 text-xs text-gray-600 min-w-0 overflow-hidden">
                    {hasDate && (
                      <span className="inline-flex items-center gap-1 flex-shrink-0">
                        <svg className="w-3 h-3 flex-shrink-0" style={{ color }} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        {dateRange}
                      </span>
                    )}
                    {hasDate && locations.length > 0 && (
                      <span className="text-gray-300 select-none flex-shrink-0">•</span>
                    )}
                    {locations.length > 0 && (
                      <LocationsRow
                        key={locations.map(loc => typeof loc === 'string' ? loc : loc.name).join(',')}
                        locations={locations.map(loc => typeof loc === 'string' ? loc : loc.name)}
                        color={color}
                      />
                    )}
                  </div>
                )
              })()}

              {/* Row 2: Rate • Budget — icon + value, no labels */}
              {(() => {
                const color = project.color || '#6B7280'
                const rateLabel = (() => {
                  if (!project.billable || !project.hourlyRate) return null
                  const allRates = [
                    project.hourlyRate,
                    ...(project.locations ?? []).map(l => l.hourlyRate).filter((r): r is number => r !== undefined)
                  ]
                  const min = Math.min(...allRates)
                  const max = Math.max(...allRates)
                  return min === max
                    ? formatHourlyRate(min, project.currency)
                    : `${formatCurrency(min, project.currency)} – ${formatHourlyRate(max, project.currency)}`
                })()
                const budgetLabel = project.budget
                  ? project.budgetType === 'hours'
                    ? `${project.budget} h/d ± ${((project.budgetHourLimit || 60) / 60).toFixed(1)} h`
                    : formatCurrency(project.budget, project.currency)
                  : null

                if (!rateLabel && !budgetLabel) {
                  return (
                    <div className="mb-4">
                      {!project.billable && <span className="text-xs text-gray-400 italic">Non-billable</span>}
                    </div>
                  )
                }
                return (
                  <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mb-4 text-xs text-gray-600">
                    {rateLabel && (
                      <span className="inline-flex items-center gap-1">
                        <svg className="w-3 h-3 flex-shrink-0" style={{ color }} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                          <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                        </svg>
                        {rateLabel}
                      </span>
                    )}
                    {rateLabel && budgetLabel && <span className="text-gray-300 select-none">•</span>}
                    {budgetLabel && (
                      <span className="inline-flex items-center gap-1">
                        <svg className="w-3 h-3 flex-shrink-0" style={{ color }} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                          {project.budgetType === 'hours' ? (
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                          ) : (
                            <>
                              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                              <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                            </>
                          )}
                        </svg>
                        {budgetLabel}
                      </span>
                    )}
                  </div>
                )
              })()}

              {/* Budget Chart - Full Width */}
              <div className="mb-4">
                <ProjectBudgetChart
                  project={project}
                  timeEntries={timeEntries}
                  getCurrentDuration={getCurrentDuration}
                  budgetWarningColor={budgetWarningColor}
                />
              </div>

              <div className="flex items-center justify-between text-xs text-gray-500 mt-auto">
                <div className="flex items-center space-x-2">
                  <span>Created {formatDate(project.createdAt)}</span>
                  <span>•</span>
                  <span className="font-medium text-gray-700">{getProjectTotalHours(project.id)}</span>
                </div>
                <div className="flex items-center space-x-2">
                  {project.type === 'shared' && (
                    <span className="text-purple-600 bg-purple-100 px-2 py-1 rounded-full text-xs">
                      Shared
                    </span>
                  )}
                  {project.billable && (
                    <span className="text-green-600">Billable</span>
                  )}
                  {project.status === 'archived' && (
                    <span className="text-orange-600">Archived</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && createPortal(
        <ProjectForm
          mode="create"
          onSubmit={handleCreate}
          onCancel={() => setShowForm(false)}
          loading={loading}
          timeEntries={timeEntries}
          getCurrentDuration={getCurrentDuration}
          formatDuration={formatDuration}
        />,
        document.body
      )}

      {editingProject && createPortal(
        <ProjectForm
          mode="edit"
          project={editingProject}
          onSubmit={handleUpdate}
          onCancel={() => setEditingProject(null)}
          loading={loading}
          timeEntries={timeEntries}
          getCurrentDuration={getCurrentDuration}
          formatDuration={formatDuration}
        />,
        document.body
      )}

      <ConfirmationDialog
        isOpen={deleteDialog.isOpen}
        title={(() => {
          switch (deleteDialog.type) {
            case 'archive':
              return 'Archive Project'
            case 'delete':
              return 'Delete Project'
            case 'restore':
              return 'Restore Project'
            case 'hardDelete':
              return 'Permanently Delete Project'
            default:
              return 'Confirm Action'
          }
        })()}
        message={(() => {
          if (!deleteDialog.project) return ''
          switch (deleteDialog.type) {
            case 'archive':
              return `"${deleteDialog.project.name}" has time entries and will be archived. Continue?`
            case 'delete':
              return `Are you sure you want to permanently delete "${deleteDialog.project.name}"?`
            case 'restore':
              return `Restore project "${deleteDialog.project.name}" to active?`
            case 'hardDelete':
              return `Permanently delete archived project "${deleteDialog.project.name}"? This cannot be undone.`
            default:
              return 'Are you sure you want to proceed?'
          }
        })()}
        confirmText={(() => {
          switch (deleteDialog.type) {
            case 'archive':
              return 'Archive'
            case 'delete':
              return 'Delete'
            case 'restore':
              return 'Restore'
            case 'hardDelete':
              return 'Delete Permanently'
            default:
              return 'Confirm'
          }
        })()}
        variant={(() => {
          switch (deleteDialog.type) {
            case 'archive':
            case 'delete':
            case 'hardDelete':
              return 'danger'
            case 'restore':
              return 'info'
            default:
              return 'danger'
          }
        })()}
        onConfirm={handleConfirmAction}
        onCancel={handleCancelAction}
        loading={loading}
      />
    </div>
  )
} 